import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminLayout } from '../components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Copy, Trash2, Link, Clock, CheckCircle, XCircle, Users, UserPlus, Phone, FileText, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminInvitationsPage = () => {
    const { language } = useLanguage();
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    
    // Form states
    const [clientName, setClientName] = useState('');
    const [phone, setPhone] = useState('');
    const [notes, setNotes] = useState('');
    const [expiryDays, setExpiryDays] = useState('7');

    useEffect(() => {
        fetchInvitations();
    }, []);

    const getAuthConfig = () => {
        const token = localStorage.getItem('nusatrade_token');
        return { headers: { 'Authorization': `Bearer ${token}` } };
    };

    const fetchInvitations = async () => {
        try {
            const response = await axios.get(`${API}/admin/invitations`, getAuthConfig());
            setInvitations(response.data);
        } catch (error) {
            console.error('Error fetching invitations:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setClientName('');
        setPhone('');
        setNotes('');
        setExpiryDays('7');
    };

    const handleCreate = async () => {
        setCreating(true);
        try {
            const token = localStorage.getItem('nusatrade_token');
            if (!token) {
                toast.error(language === 'id' ? 'Sila login semula' : 'Please login again');
                setCreating(false);
                return;
            }
            
            await axios.post(`${API}/admin/invitations`, {
                client_name: clientName,
                phone: phone,
                notes: notes,
                expires_in_days: parseInt(expiryDays)
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            toast.success(language === 'id' ? 'Link invitation dibuat!' : 'Invitation link created!');
            setDialogOpen(false);
            resetForm();
            fetchInvitations();
        } catch (error) {
            console.error('Create invitation error:', error);
            const message = error.response?.data?.detail || 'Error creating invitation';
            toast.error(message);
        } finally {
            setCreating(false);
        }
    };

    const handleToggle = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            await axios.put(`${API}/admin/invitations/${id}/toggle`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success(currentStatus 
                ? (language === 'id' ? 'Link ditutup!' : 'Link closed!')
                : (language === 'id' ? 'Link dibuka!' : 'Link opened!')
            );
            fetchInvitations();
        } catch (error) {
            toast.error('Error toggling invitation');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm(language === 'id' ? 'Yakin hapus invitation ini?' : 'Delete this invitation?')) return;
        try {
            const token = localStorage.getItem('nusatrade_token');
            await axios.delete(`${API}/admin/invitations/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success(language === 'id' ? 'Invitation dihapus!' : 'Invitation deleted!');
            fetchInvitations();
        } catch (error) {
            toast.error('Error deleting invitation');
        }
    };

    const copyToClipboard = (code) => {
        const url = `${window.location.origin}/register?invite=${code}`;
        navigator.clipboard.writeText(url);
        toast.success(language === 'id' ? 'Link disalin!' : 'Link copied!');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Open</Badge>;
            case 'closed':
                return <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">Closed</Badge>;
            case 'expired':
                return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Expired</Badge>;
            default:
                return <Badge className="bg-slate-500/20 text-slate-400">Unknown</Badge>;
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const stats = {
        total: invitations.length,
        active: invitations.filter(i => i.status === 'active').length,
        closed: invitations.filter(i => i.status === 'closed').length,
        expired: invitations.filter(i => i.status === 'expired').length,
        totalUsage: invitations.reduce((sum, i) => sum + (i.usage_count || 0), 0)
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
                            <UserPlus className="w-6 h-6 text-primary" />
                            {language === 'id' ? 'Kelola Invitation' : 'Manage Invitations'}
                        </h1>
                        <p className="text-slate-400 mt-1">
                            {language === 'id' ? 'Setiap link boleh digunakan unlimited orang' : 'Each link can be used by unlimited people'}
                        </p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="create-invitation-btn" className="bg-primary hover:bg-primary/90">
                                <Plus className="w-4 h-4 mr-2" />
                                {language === 'id' ? 'Generate Invite Link' : 'Generate Invite Link'}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-lg">
                                    <UserPlus className="w-5 h-5 text-primary" />
                                    {language === 'id' ? 'Generate Invite Link Baru' : 'Generate New Invite Link'}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                                {/* Nama Campaign */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300">
                                        {language === 'id' ? 'Nama Campaign/Group (Opsional)' : 'Campaign/Group Name (Optional)'}
                                    </Label>
                                    <Input
                                        value={clientName}
                                        onChange={(e) => setClientName(e.target.value)}
                                        placeholder={language === 'id' ? 'Contoh: Keluarga Markas, Batch 1' : 'Example: Family Group, Batch 1'}
                                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                                        data-testid="client-name-input"
                                    />
                                </div>

                                {/* No Telefon */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 flex items-center gap-2">
                                        <Phone className="w-4 h-4" />
                                        {language === 'id' ? 'No. Telefon (Opsional)' : 'Phone Number (Optional)'}
                                    </Label>
                                    <Input
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+60123456789"
                                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                                        data-testid="phone-input"
                                    />
                                </div>

                                {/* Nota */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        {language === 'id' ? 'Nota (Opsional)' : 'Notes (Optional)'}
                                    </Label>
                                    <Textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder={language === 'id' ? 'Contoh: Referral dari IB Fariz' : 'Example: Referral from IB Fariz'}
                                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 resize-none"
                                        rows={2}
                                        data-testid="notes-input"
                                    />
                                </div>

                                {/* Tarikh Luput */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        {language === 'id' ? 'Tarikh Luput' : 'Expiry Date'}
                                    </Label>
                                    <Select value={expiryDays} onValueChange={setExpiryDays}>
                                        <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="expiry-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-800 border-slate-700">
                                            <SelectItem value="7" className="text-white hover:bg-slate-700">7 {language === 'id' ? 'hari' : 'days'}</SelectItem>
                                            <SelectItem value="14" className="text-white hover:bg-slate-700">14 {language === 'id' ? 'hari' : 'days'}</SelectItem>
                                            <SelectItem value="30" className="text-white hover:bg-slate-700">30 {language === 'id' ? 'hari' : 'days'}</SelectItem>
                                            <SelectItem value="60" className="text-white hover:bg-slate-700">60 {language === 'id' ? 'hari' : 'days'}</SelectItem>
                                            <SelectItem value="90" className="text-white hover:bg-slate-700">90 {language === 'id' ? 'hari' : 'days'}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Info */}
                                <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-400">
                                    <p>💡 Link ini boleh digunakan oleh <strong className="text-white">unlimited</strong> orang sehingga tarikh luput atau anda tutup secara manual.</p>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={() => setDialogOpen(false)}
                                        className="flex-1 bg-transparent border-slate-700 text-white hover:bg-slate-800"
                                    >
                                        {language === 'id' ? 'Batal' : 'Cancel'}
                                    </Button>
                                    <Button 
                                        onClick={handleCreate} 
                                        className="flex-1 bg-primary hover:bg-primary/90" 
                                        disabled={creating}
                                        data-testid="confirm-create-btn"
                                    >
                                        {creating ? 'Creating...' : (language === 'id' ? 'Generate Link' : 'Generate Link')}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="bg-slate-800/50 border-slate-700">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                                    <Link className="w-5 h-5 text-slate-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white">{stats.total}</p>
                                    <p className="text-xs text-slate-400">Total Links</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-emerald-500/10 border-emerald-500/20">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-emerald-400">{stats.active}</p>
                                    <p className="text-xs text-slate-400">Open</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-slate-500/10 border-slate-500/20">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center">
                                    <XCircle className="w-5 h-5 text-slate-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-slate-400">{stats.closed}</p>
                                    <p className="text-xs text-slate-400">Closed</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-red-500/10 border-red-500/20">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-red-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-red-400">{stats.expired}</p>
                                    <p className="text-xs text-slate-400">Expired</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-blue-500/10 border-blue-500/20">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                    <Users className="w-5 h-5 text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-blue-400">{stats.totalUsage}</p>
                                    <p className="text-xs text-slate-400">Total Signups</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Invitations List */}
                <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                        <CardTitle className="text-white">
                            {language === 'id' ? 'Daftar Invitation Links' : 'Invitation Links'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {invitations.length === 0 ? (
                            <div className="text-center py-8">
                                <Link className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <p className="text-slate-400">
                                    {language === 'id' ? 'Belum ada invitation' : 'No invitations yet'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {invitations.map((inv) => (
                                    <div 
                                        key={inv.id}
                                        className={`flex items-center justify-between p-4 rounded-xl border ${
                                            inv.status === 'active' 
                                                ? 'bg-slate-900/50 border-emerald-500/30' 
                                                : inv.status === 'closed'
                                                ? 'bg-slate-900/50 border-slate-600'
                                                : 'bg-slate-900/30 border-slate-700/50'
                                        }`}
                                        data-testid={`invitation-${inv.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                                                inv.status === 'active' ? 'bg-emerald-500/20' : 'bg-slate-700/50'
                                            }`}>
                                                <span className={`font-mono font-bold text-sm ${
                                                    inv.status === 'active' ? 'text-emerald-400' : 'text-slate-400'
                                                }`}>{inv.code}</span>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-medium text-white">
                                                        {inv.client_name || `Link ${inv.code}`}
                                                    </span>
                                                    {getStatusBadge(inv.status)}
                                                    <Badge className="bg-blue-500/20 text-blue-400 border-0">
                                                        <Users className="w-3 h-3 mr-1" />
                                                        {inv.usage_count || 0} signups
                                                    </Badge>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        Expires: {formatDate(inv.expires_at)}
                                                    </span>
                                                    {inv.phone && (
                                                        <span className="flex items-center gap-1">
                                                            <Phone className="w-3 h-3" />
                                                            {inv.phone}
                                                        </span>
                                                    )}
                                                </div>
                                                {inv.notes && (
                                                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                                                        <FileText className="w-3 h-3" />
                                                        {inv.notes}
                                                    </p>
                                                )}
                                                {inv.used_by_list && inv.used_by_list.length > 0 && (
                                                    <p className="text-xs text-slate-500 mt-1">
                                                        Used by: {inv.used_by_list.slice(0, 3).join(', ')}
                                                        {inv.used_by_list.length > 3 && ` +${inv.used_by_list.length - 3} more`}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {/* Toggle Open/Close */}
                                            {inv.status !== 'expired' && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-slate-400">
                                                        {inv.status === 'active' ? 'Open' : 'Closed'}
                                                    </span>
                                                    <Switch
                                                        checked={inv.status === 'active'}
                                                        onCheckedChange={() => handleToggle(inv.id, inv.status === 'active')}
                                                        data-testid={`toggle-${inv.id}`}
                                                    />
                                                </div>
                                            )}
                                            {inv.status === 'active' && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => copyToClipboard(inv.code)}
                                                    className="bg-slate-700 hover:bg-slate-600 text-white"
                                                    data-testid={`copy-${inv.id}`}
                                                >
                                                    <Copy className="w-4 h-4 mr-1" />
                                                    Copy
                                                </Button>
                                            )}
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => handleDelete(inv.id)}
                                                className="text-slate-400 hover:text-red-400 hover:bg-red-900/20"
                                                data-testid={`delete-${inv.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};
